import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { DocumentCategoryService } from './document-category.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import Swal from 'sweetalert2';
import { IDocumentCategory } from './document-category.interface';
import { EditDocumentCategoryComponent } from './edit-document-category/edit-document-category.component';
import { AddDocumentCategoryComponent } from './add-document-category/add-document-category.component';

@Component({
    selector: 'app-document-category',
    templateUrl: './document-category.component.html',
    styleUrls: ['./document-category.component.scss']
})
export class DocumentCategoryComponent {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;
    isLoading = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink =
        environment.interop.basePath +
        environment.interop.deps.nomenclatoare.cerereDocumentCategory.listDT;

    constructor(
        public permissionsService: PermissionsService,
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private documentTypeService: DocumentCategoryService
    ) { }

    ngOnInit(): void {
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            responsive: true,
            pageLength: 25,
            searching: false,
            serverSide: true,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Nume',
                    data: 'name',
                },
                {
                    title: 'Slug',
                    data: 'slug',
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button type="button" class="action-btn edit me-1">
                                <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                            </button>
                            <button type="button" class="action-btn delete me-1">
                                <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                            </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'deps-module.settings.categorii-documente.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'deps-module.settings.categorii-documente.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteDocumentCategory(id);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddDocumentCategoryComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Categorie document adăugată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: IDocumentCategory) {
        const dialogRef = this.dialog.open(EditDocumentCategoryComponent, {
            ...this.dialogService.getDialogConfig(),
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Actualizare cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteDocumentCategory(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                // we do this because we need to have some control on activity
                this.deleteDocumentCategoryAction(id);
            }
        });
    }

    deleteDocumentCategoryAction(id: number) {
        this.isLoading = true;
        this.documentTypeService.deleteDocumentCategory(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Categorie document ștearsă cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: { error: { errors: { message: any[] } } }) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error.errors.message[0];
                } catch (error) {
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteDocumentCategoryAction(id);
                    }
                });
            }
        );
    }

    clearCache() {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.cleanCache,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.documentTypeService.clearCache().subscribe(() => {
                    this.rerenderDT();
                    Swal.fire({
                        title: helper.swalTextConfig.headers.deleted,
                        text: helper.swalTextConfig.generalMessages.cleanCacheSuccess,
                        icon: 'success',
                        confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                    });
                });
            }
        });
    }
}
